import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Evaluation } from '../types/api';
import SectionCard from './SectionCard';
import '../styles/components/EvaluationPanel.css';
import SWOTGroup from './SWOTGroup';
import NavigationTabs, { TabItem } from './common/NavigationTabs';
import EvaluationSummary from './EvaluationSummary';
import { postData, fetchData, api } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';

interface EvaluationPanelProps {
  evaluation: Evaluation;
  onTimeUpdate?: (time: number) => void;
  isEditable?: boolean;
  onEvaluationUpdate?: () => void;
}

// Define types for SWOT items since they're missing from the Evaluation type
interface SWOTItem {
  id: number;
  finding: string;
  swot_type_id: number;
  section_id?: number;
}

// Add this interface definition for match positions
interface MatchPosition {
  element: Element;
  top: number;
}

interface MomentOperationInterface {
  id: number;
  name: string;
  description: string;
}

interface MomentItem {
  id: number;
  name: string;
  description?: string;
  category_id?: number;
}

interface MomentsResponse {
  moments: MomentItem[];
}

interface MomentOperationResponse {
  operations: MomentOperationInterface[];
}

const EvaluationPanel: React.FC<EvaluationPanelProps> = ({ evaluation, onTimeUpdate, isEditable = true, onEvaluationUpdate }) => {
  // Define all hooks at the top of the component
  const [expandedEvaluationInsights, setExpandedEvaluationInsights] = useState(true);
  const [showNewInsightForm, setShowNewInsightForm] = useState<{[key: string]: boolean}>({});
  const [showOverallInsightForm, setShowOverallInsightForm] = useState(false);
  const [newInsightText, setNewInsightText] = useState('');
  const [newInsightType, setNewInsightType] = useState<number>(1);
  const [selectedSectionForOverall, setSelectedSectionForOverall] = useState<string | null>(null);
  
  // Search related state
  const [searchText, setSearchText] = useState('');
  const [matchPositions, setMatchPositions] = useState<MatchPosition[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  // Section score management
  const [sectionScores, setSectionScores] = useState<Record<number, number>>({});
  
  // Global editing state (only one question across all sections can be edited)
  const [currentlyEditingQuestion, setCurrentlyEditingQuestion] = useState<{
    sectionId: number;
    questionId: number;
  } | null>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
  const [reasonText, setReasonText] = useState<string>('');
  const [pendingSelections, setPendingSelections] = useState<Record<number, boolean>>({});

  // Initialize section scores from evaluation data
  React.useEffect(() => {
    const initialScores: Record<number, number> = {};
    evaluation.sections.forEach(section => {
      initialScores[section.id] = section.points;
    });
    setSectionScores(initialScores);
  }, [evaluation.sections]);

  // Handle section score changes
  const handleSectionScoreChange = useCallback((sectionId: number, newScore: number) => {
    setSectionScores(prev => ({
      ...prev,
      [sectionId]: newScore
    }));
  }, []);

  // Global editing handlers
  const handlePendingSelectionsChange = useCallback((sectionId: number, questionId: number, selections: Record<number, boolean>) => {
    // Always update selections - we control editing state in requestEditPermission
    setPendingSelections(selections);
  }, []);

  const handleHasPendingChangesChange = useCallback((sectionId: number, questionId: number, hasChanges: boolean) => {
    // Only update if this is the currently editing question
    if (currentlyEditingQuestion?.sectionId === sectionId && currentlyEditingQuestion?.questionId === questionId) {
      setHasPendingChanges(hasChanges);
    }
  }, [currentlyEditingQuestion]);

  const handleReasonTextChange = useCallback((sectionId: number, questionId: number, reason: string) => {
    // Only update if this is the currently editing question
    if (currentlyEditingQuestion?.sectionId === sectionId && currentlyEditingQuestion?.questionId === questionId) {
      setReasonText(reason);
    }
  }, [currentlyEditingQuestion]);

  // Request permission to edit a question
  const requestEditPermission = useCallback((sectionId: number, questionId: number): boolean => {
    if (currentlyEditingQuestion &&
        (currentlyEditingQuestion.sectionId !== sectionId || currentlyEditingQuestion.questionId !== questionId)) {
      return false; // Another question is being edited
    }

    // Update all editing state synchronously
    const newEditingState = { sectionId, questionId };
    setCurrentlyEditingQuestion(newEditingState);
    
    // Remove the condition in handlePendingSelectionsChange by setting state directly
    setPendingSelections({});
    setHasPendingChanges(false);
    setReasonText('');
    return true;
  }, [currentlyEditingQuestion]);

  // Release edit permission when done editing
  const releaseEditPermission = useCallback((sectionId: number, questionId: number) => {
    if (currentlyEditingQuestion?.sectionId === sectionId && currentlyEditingQuestion?.questionId === questionId) {
      setCurrentlyEditingQuestion(null);
    }
  }, [currentlyEditingQuestion]);

  // Reset pending changes for the currently editing question
  const resetPendingChanges = useCallback(() => {
    setHasPendingChanges(false);
    setReasonText('');
    setPendingSelections({});
    setCurrentlyEditingQuestion(null);
  }, []);

  // Calculate total score from section scores
  const totalScore = Object.values(sectionScores).reduce((sum, score) => sum + score, 0);

  // Get SWOT data with type assertion
  const evaluationSWOT: SWOTItem[] = ((evaluation as any).swot || []);

  // Load operations
  const [operations, setOperations] = useState<MomentOperationInterface[]>([]);
  const [moments, setMoments] = useState<MomentItem[]>([]);

  // Initialize all sections as expanded by default with null checking
  useEffect(() => {
    const loadData = async () => {
      // Load operations
      try {
        const operationsResponse = await fetchData<MomentOperationResponse>(api(PATH_SEGMENTS.MOMENT_OPERATION));
        setOperations(operationsResponse.operations || []);
      } catch (err) {
        console.error("Error loading operations:", err);
        setOperations([]);
      }
      
      // Load moments
      try {
        const momentsResponse = await fetchData<MomentsResponse>(api(PATH_SEGMENTS.MOMENT));
        console.log("momentsResponse", momentsResponse.moments);
        setMoments(momentsResponse.moments || []);
      } catch (err) {
        console.error("Error loading moments:", err);
        setMoments([]);
      }
    }

    loadData();

    if (!evaluation?.sections) return;
    

  }, [evaluation.sections]);

  // Enhanced search function to also expand relevant sections
  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
    
    if (text && evaluation) {
      // Check if we need to expand any sections based on the search
      evaluation.sections?.forEach(section => {
        // Check section name
        if (section.form_section.name.toLowerCase().includes(text.toLowerCase())) {
          // Section will be expanded by default in SectionCard
        }
        
        // Check questions within section
        section.questions?.forEach(question => {
          if (
            question.form_question.prompt.toLowerCase().includes(text.toLowerCase()) ||
            (question.remarks && question.remarks.toLowerCase().includes(text.toLowerCase()))
          ) {
            // Section will be expanded by default in SectionCard
          }
        });
        
        // Check insights within section
        if (section.swot && section.swot.length > 0) {
          const hasMatch = section.swot.some(item => 
            item.finding.toLowerCase().includes(text.toLowerCase())
          );
          
          if (hasMatch) {
            // Section will be expanded by default in SectionCard
          }
        }
      });
      
      // Also check overall insights with type assertion
      const evaluationSwot = (evaluation as any).swot;
      if (evaluationSwot && evaluationSwot.length > 0) {
        const hasMatch = evaluationSwot.some((item: SWOTItem) => 
          item.finding.toLowerCase().includes(text.toLowerCase())
        );
        
        if (hasMatch) {
          setExpandedEvaluationInsights(true);
        }
      }
      
      // Set a small timeout to let React render the highlights first
      setTimeout(() => {
        const highlights = panelRef.current?.querySelectorAll('.search-highlight') || [];
        
        if (highlights.length > 0) {
          const positions = Array.from(highlights).map(el => {
            // Get position info for scrolling
            const rect = el.getBoundingClientRect();
            return {
              element: el,
              top: rect.top
            };
          });
          
          setMatchPositions(positions);
          setCurrentMatchIndex(0);
          
          // Scroll to first match
          if (positions.length > 0 && positions[0].element) {
            positions[0].element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else {
          setMatchPositions([]);
          setCurrentMatchIndex(-1);
        }
      }, 200); // Increased timeout to ensure expanded content is rendered
    } else {
      setMatchPositions([]);
      setCurrentMatchIndex(-1);
    }
  }, [evaluation]);

  // Navigate to next match
  const goToNextMatch = useCallback(() => {
    if (matchPositions.length > 0) {
      const nextIndex = (currentMatchIndex + 1) % matchPositions.length;
      setCurrentMatchIndex(nextIndex);
      scrollToMatch(nextIndex);
    }
  }, [matchPositions, currentMatchIndex]);

  // Navigate to previous match
  const goToPreviousMatch = useCallback(() => {
    if (matchPositions.length > 0) {
      const prevIndex = (currentMatchIndex - 1 + matchPositions.length) % matchPositions.length;
      setCurrentMatchIndex(prevIndex);
      scrollToMatch(prevIndex);
    }
  }, [matchPositions, currentMatchIndex]);

  // Scroll to a specific match
  const scrollToMatch = (index: number) => {
    if (matchPositions[index]?.element) {
      matchPositions[index].element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };





  const toggleEvaluationInsights = () => {
    setExpandedEvaluationInsights(!expandedEvaluationInsights);
  };

  const toggleNewInsightForm = (sectionId: string) => {
    setShowNewInsightForm(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
    setNewInsightText('');
    setNewInsightType(1);
  };

  const toggleOverallInsightForm = () => {
    setShowOverallInsightForm(!showOverallInsightForm);
    setNewInsightText('');
    setNewInsightType(1);
    setSelectedSectionForOverall(evaluation.sections[0]?.id ? String(evaluation.sections[0].id) : null);
  };

  const handleAddInsight = (sectionId: string) => {
    // Add insight logic would go here (API call)
    console.log(`Adding insight to section ${sectionId}: ${newInsightText} (Type: ${newInsightType})`);
    toggleNewInsightForm(sectionId);
  };

  const handleAddOverallInsight = () => {
    // Add overall insight logic would go here (API call)
    console.log(`Adding overall insight: ${newInsightText} (Type: ${newInsightType}, Section: ${selectedSectionForOverall})`);
    toggleOverallInsightForm();
  };

  // Group SWOT items by type
  const groupSWOTByType = (items: any[]) => {
    const grouped: { [key: number]: any[] } = {
      1: [], // Strengths
      2: [], // Weaknesses
      3: [], // Opportunities
      4: []  // Threats
    };
    
    items.forEach(item => {
      if (grouped[item.swot_type_id]) {
        grouped[item.swot_type_id].push(item);
      }
    });
    
    return grouped;
  };
  
  const handleEditSWOT = (item: SWOTItem) => {
    // Find the section containing this SWOT item
    const section = evaluation.sections.find(s => s.swot?.some(swot => swot.id === item.id));
    if (!section) return;

    // Update the SWOT item in the section
    const updatedSections = evaluation.sections.map(s => {
      if (s.id === section.id) {
        return {
          ...s,
          swot: s.swot?.map(swot => 
            swot.id === item.id ? { ...swot, ...item } : swot
          )
        };
      }
      return s;
    });

    // Update the evaluation state
    const updatedEvaluation = {
      ...evaluation,
      sections: updatedSections
    };

    // Call the API to update the SWOT item
    postData(`/api/evaluations/${evaluation.id}/swot/${item.id}`, item)
      .catch((error: Error) => {
        console.error('Error updating SWOT item:', error);
      });
  };
  
  const handleDeleteSWOT = (itemId: number) => {
    // Find the section containing this SWOT item
    const section = evaluation.sections.find(s => s.swot?.some(swot => swot.id === itemId));
    if (!section) return;

    // Remove the SWOT item from the section
    const updatedSections = evaluation.sections.map(s => {
      if (s.id === section.id) {
        return {
          ...s,
          swot: s.swot?.filter(swot => swot.id !== itemId)
        };
      }
      return s;
    });

    // Update the evaluation state
    const updatedEvaluation = {
      ...evaluation,
      sections: updatedSections
    };


    // Call the API to delete the SWOT item
    postData(`/api/evaluations/${evaluation.id}/swot/${itemId}/delete`, {})
      .then(() => {
        // Update the parent component's state with the new evaluation
        if (onTimeUpdate) {
          onTimeUpdate(Date.now()); // Force a re-render
        }
      })
      .catch((error: Error) => {
        console.error('Error deleting SWOT item:', error);
        // Revert the local state if the API call fails
        const revertedSections = evaluation.sections.map(s => {
          if (s.id === section.id) {
            return {
              ...s,
              swot: s.swot
            };
          }
          return s;
        });
        const revertedEvaluation = {
          ...evaluation,
          sections: revertedSections
        };
        // Update the parent component's state back to the original
        if (onTimeUpdate) {
          onTimeUpdate(Date.now());
        }
      });
  };

  const handleDeleteQuestionRemarks = (questionId: string) => {
    // Add your API call logic here to delete the remarks
    console.log(`Deleting remarks for question ID: ${questionId}`);
  };

  // Define tabs for NavigationTabs
  const tabItems: TabItem[] = [
    {
      id: 'evaluation',
      // label: `Evaluation: ${evaluation?.points}/${evaluation.evalform.points}`,
      // label: `Evaluation`,
      label: evaluation?.form?.name || "Call Score",
      icon: 'assessment',
      searchable: true
    }
  ];



  // Ensure SWOTGroup component receives search text for highlighting
  const renderSWOTGroups = (swotItems: any[], sectionId?: string) => {
    if (!swotItems || swotItems.length === 0) return null;
    
    const grouped = groupSWOTByType(swotItems);
    
    return Object.entries(grouped).map(([typeId, items]) => (
      items.length > 0 && (
        <SWOTGroup
          key={typeId}
          type={Number(typeId)}
          items={items}
          evaluationId={String(evaluation?.id)}
          searchText={searchText} // Pass search text
          onEdit={handleEditSWOT}
          onDelete={handleDeleteSWOT}
        />
      )
    ));
  };

  // Add highlight capability to section insights content
  const renderSectionInsights = (section: any) => {
    if (!section.swot || section.swot.length === 0) {
      return <div className="no-insights">No insights available</div>;
    }
    
    return renderSWOTGroups(section.swot, String(section.id));
  };

  // Add these functions to handle navigation
  const navigateToQuestion = useCallback((questionId: number) => {
    // Find the section containing this question
    for (const section of evaluation?.sections || []) {
      const question = section.questions.find(q => q.id === questionId);
      if (question) {
        // Section will be expanded by default in SectionCard
        
        // Scroll to the question after a short delay to allow expansion
        setTimeout(() => {
          const questionElement = document.getElementById(`question-${questionId}`);
          if (questionElement) {
            questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Highlight the element temporarily
            questionElement.classList.add('highlight-target');
            setTimeout(() => {
              questionElement.classList.remove('highlight-target');
            }, 2000);
          } else {
            console.log("Question element not found:", `question-${questionId}`);
          }
        }, 100);
        break;
      }
    }
  }, [evaluation?.sections]);

  const navigateToSWOT = useCallback((swotId: number) => {
    const swotItems = [...((evaluation as any)?.swot || [])];
    
    // Check in overall evaluation insights
    const overallSwot = swotItems.find(item => item.id === swotId);
    if (overallSwot) {
      setExpandedEvaluationInsights(true);
      
      // Scroll to the SWOT item
      setTimeout(() => {
        const swotElement = document.getElementById(`swot-${swotId}`);
        if (swotElement) {
          swotElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Highlight the element temporarily
          swotElement.classList.add('highlight-target');
          setTimeout(() => {
            swotElement.classList.remove('highlight-target');
          }, 2000);
        } else {
          console.log("SWOT element not found:", `swot-${swotId}`);
        }
      }, 100);
      return;
    }
    
    // Check in section insights
    for (const section of evaluation?.sections || []) {
      if (!section.swot) continue;
      
      const sectionSwot = section.swot.find((item: any) => item.id === swotId);
      if (sectionSwot) {
        // Section will be expanded by default in SectionCard
        
        // Scroll to the SWOT item
        setTimeout(() => {
          const swotElement = document.getElementById(`swot-${swotId}`);
          if (swotElement) {
            swotElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Highlight the element temporarily
            swotElement.classList.add('highlight-target');
            setTimeout(() => {
              swotElement.classList.remove('highlight-target');
            }, 2000);
          } else {
            console.log("SWOT element not found:", `swot-${swotId}`);
          }
        }, 100);
        break;
      }
    }
    
  }, [evaluation]);



  // Add this function to the component
  const handleEvidenceClick = (startTime: string) => {
    console.log("Evidence clicked with time:", startTime);
    
    // Handle different time formats (HH:MM:SS or MM:SS)
    let timeInSeconds = 0;
    const parts = startTime.split(':').map(Number);
    
    if (parts.length === 3) {
      // HH:MM:SS format
      timeInSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      // MM:SS format
      timeInSeconds = parts[0] * 60 + parts[1];
    }
    
    console.log("Converted to seconds:", timeInSeconds);
    
    // Call parent function to update audio position and scroll transcript
    if (typeof onTimeUpdate === 'function') {
      console.log("handleEvidenceClick: calling parent onTimeUpdate");
      onTimeUpdate(timeInSeconds*1000);
    }
  };

  // Return the updated component with NavigationTabs
  return (
    <div className="evaluation-panel">
      {/* <div className="evaluation-header">
        <h2>{evaluation.form?.name || "Evaluation"}</h2>
        <div className="total-score">
          {evaluation.points} / {evaluation.form?.points}
        </div>
      </div> */}
      <NavigationTabs
        tabs={tabItems}
        activeTab="evaluation"
        onTabChange={() => {}} // No-op since we only have one tab
        searchValue={searchText}
        onSearch={handleSearch}
        onNavigateNext={goToNextMatch}
        onNavigatePrevious={goToPreviousMatch}
        searchStats={
          matchPositions.length > 0
            ? {
                currentMatch: currentMatchIndex + 1,
                totalMatches: matchPositions.length
              }
            : undefined
        }
      />
     
      
      <div className="sections-container" ref={panelRef}>
        {evaluation && (
          <EvaluationSummary
            evaluationScore={totalScore}
            maxScore={evaluation.form?.points} // Adjust if your max score is different
            questions={evaluation.sections.flatMap(section => section.questions)}
            swotItems={[
              ...(evaluation as any).swot || [],
              ...evaluation.sections.flatMap(section => section.swot || [])
            ]}
            showSummary={false}
            onNavigateToQuestion={navigateToQuestion}
            onNavigateToSWOT={navigateToSWOT}
          />
        )}
        
        {evaluation.sections.map((section) => (
          <SectionCard
            key={section.id}
            id={section.id}
            title={section.form_section.name}
            points={section.points}
            totalPoints={section.form_section.points}
            questions={section.questions}
            swotItems={section.swot || []}
            evaluationId={String(evaluation.id)}
            searchText={searchText}
            onEvidenceClick={handleEvidenceClick}
            moments={moments}
            operations={operations}
            onRemarksChange={(questionId: string | number, remarks: string) => {
              // TODO: Implement remarks change handler
              console.log(`Remarks changed for question ${questionId}: ${remarks}`);
            }}
            isEditable={isEditable}
            onEvaluationUpdate={onEvaluationUpdate}
            onSectionScoreChange={handleSectionScoreChange}
            // Global editing state
            currentlyEditingQuestion={currentlyEditingQuestion}
            hasPendingChanges={hasPendingChanges}
            reasonText={reasonText}
            pendingSelections={pendingSelections}
            onPendingSelectionsChange={handlePendingSelectionsChange}
            onHasPendingChangesChange={handleHasPendingChangesChange}
            onReasonTextChange={handleReasonTextChange}
            onRequestEditPermission={requestEditPermission}
            onReleaseEditPermission={releaseEditPermission}
            onResetPendingChanges={resetPendingChanges}
          />
        ))}
        
        {evaluationSWOT.length > 0 && (
          <div className="evaluation-insights-separator">
            <div 
              className="evaluation-insights-header"
              onClick={toggleEvaluationInsights}
            >
              <h4>Evaluation Insights</h4>
              <span className="material-icons">
                {expandedEvaluationInsights ? 'expand_less' : 'expand_more'}
              </span>
            </div>
            
            {expandedEvaluationInsights && (
              <div className="evaluation-insights-content">
                {Object.entries(groupSWOTByType(evaluationSWOT)).map(([typeId, items]) => (
                  items.length > 0 && (
                    <SWOTGroup
                      key={typeId}
                      type={Number(typeId)}
                      items={items}
                      evaluationId={String(evaluation.id)}
                      onEdit={handleEditSWOT}
                      onDelete={handleDeleteSWOT}
                    />
                  )
                ))}
                
                {showOverallInsightForm ? (
                  <div className="new-insight-form-container">
                    <div className="new-insight-form">
                      <div className="form-group">
                        <label>Section</label>
                        <select
                          value={selectedSectionForOverall || ""}
                          onChange={(e) => setSelectedSectionForOverall(e.target.value)}
                        >
                          {evaluation.sections.map((section) => (
                            <option key={section.id} value={section.id}>
                              {section.form_section.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Type</label>
                        <select 
                          value={newInsightType} 
                          onChange={(e) => setNewInsightType(Number(e.target.value))}
                        >
                          <option value={1}>Strength</option>
                          <option value={2}>Weakness</option>
                          <option value={3}>Opportunity</option>
                          <option value={4}>Threat</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Content</label>
                        <textarea
                          value={newInsightText}
                          onChange={(e) => setNewInsightText(e.target.value)}
                          placeholder="Enter insight content..."
                        />
                      </div>
                      <div className="form-actions">
                        <button 
                          className="cancel-button"
                          onClick={toggleOverallInsightForm}
                        >
                          <span className="material-icons">close</span>
                          Cancel
                        </button>
                        <button 
                          className="save-button"
                          disabled={!newInsightText.trim() || !selectedSectionForOverall}
                          onClick={handleAddOverallInsight}
                        >
                          <span className="material-icons">check</span>
                          Add Insight
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button 
                    className="add-insight-button"
                    onClick={toggleOverallInsightForm}
                  >
                    <span className="material-icons">add</span>
                    Add Insight
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluationPanel; 